import {enableProdMode, provideAppInitializer, inject, importProvidersFrom, provideZoneChangeDetection} from '@angular/core';
import {environment} from './environments/environment';
import {EtHttpService} from './app/services/et-http/et-http.service';
import {InAppBrowser} from '@awesome-cordova-plugins/in-app-browser/ngx';
import {Platform, IonicRouteStrategy} from '@ionic/angular';
import {FirebaseService} from './app/services/firebase/firebase.service';
import {AuthorizationService} from './app/auth/authorization.service';
import {LocalStorageService, LOCAL_STORAGE_TOKEN, localStorageFactory} from './app/services/local-storage/local-storage.service';
import {RouteReuseStrategy} from '@angular/router';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {GlobalTenantInterceptor} from './app/commons/http/interceptors/global-tenant.interceptor';
import {AuthInterceptor} from './app/commons/http/interceptors/auth.interceptor';
import {AuthExpiredInterceptor} from './app/commons/http/interceptors/auth-expired.interceptor';
import {ErrorHandlerInterceptor} from './app/commons/http/interceptors/error-handler.interceptor';
import {InternetConnectionInterceptor} from './app/commons/http/interceptors/internet-connection.interceptor';
import {DeviceInterceptor} from './app/commons/http/interceptors/device.interceptor';
import {AppVersionInterceptor} from './app/commons/http/interceptors/app-version-interceptor';
import {ClientContextInterceptor} from './app/commons/http/interceptors/client-context.interceptor';
import {SslPinningRequestInterceptor} from './app/commons/http/interceptors/ssl-pinning-request-interceptor.service';
import {SecureStorage} from '@awesome-cordova-plugins/secure-storage/ngx';
import {HTTP} from '@awesome-cordova-plugins/http/ngx';
import {ChartDataPipe} from './app/pipes/chart-date/chart-date';
import {CurrenciesService} from './app/services/currencies/currencies.service';
import {HedgingProfileService} from './app/services/hedging-profile/hedging-profile.service';
import {CompanyService} from './app/services/company/company.service';
import {CacheService} from './app/services/cache/cache.service';
import {SharedDataService} from './app/services/shared-data/shared-data.service';
import {RxStompService} from './app/services/rx-stomp/rx-stomp.service';
import {CallNumber} from '@awesome-cordova-plugins/call-number/ngx';
import {EmailComposer} from '@awesome-cordova-plugins/email-composer/ngx';
import {Clipboard} from '@awesome-cordova-plugins/clipboard/ngx';
import {AuthGuardService} from './app/auth/auth-guard.service';
import {JwtHelperService} from '@auth0/angular-jwt';
import {FingerprintAIO} from '@awesome-cordova-plugins/fingerprint-aio/ngx';
import {File} from '@awesome-cordova-plugins/file/ngx';
import {SocialSharing} from '@awesome-cordova-plugins/social-sharing/ngx';
import {Market} from '@awesome-cordova-plugins/market/ngx';
import {BrowserModule, bootstrapApplication} from '@angular/platform-browser';
import {AppRoutingModule} from './app/app-routing.module';
import {FormsModule} from '@angular/forms';
import {PipesModule} from './app/pipes/pipes.module';
import {MatDialogModule} from '@angular/material/dialog';
import {GeneralInfoPageModule} from './app/pages/general-info/general-info.module';
import {NgIdleModule} from '@ng-idle/core';
import {provideAnimations} from '@angular/platform-browser/animations';
import {provideTranslateService, TranslateLoader} from '@ngx-translate/core';
import {IonicStorageModule} from '@ionic/storage-angular';
import {AuthModule} from './app/auth/auth.module';
import {LoggerModule} from 'ngx-logger';
import {LetDirective} from '@ngrx/component';
import {AppComponent} from './app/app.component';
import {BackendTranslateLoader} from './app/commons/http/loaders/backend-translate.loader';
import {registerLocaleData} from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import localeRo from '@angular/common/locales/ro';
import localeFi from '@angular/common/locales/fi';
import localeBe from '@angular/common/locales/be';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import {Supplier} from './app/commons/functional-types';
import {HighchartsCustomizations} from './app/commons/highcharts-customizations';
import {NumberFormatService} from './app/services/number-format/number-format.service';
import {firstValueFrom} from 'rxjs';
import {register} from 'swiper/element/bundle';
import {provideIonicAngular} from '@ionic/angular/standalone';

register();

if (environment.production) {
  enableProdMode();
}

export class FixedFileReader extends window.FileReader { // Needed for capacitor FilReader issue, that extends to cordova-advanced-http's multiPartFile POST requests
  constructor() {
    super();
    const zoneOriginalInstance = (this as any).__zone_symbol__originalInstance;
    return zoneOriginalInstance || this;
  }
}

window.FileReader = FixedFileReader;

registerLocaleData(localeNl);
registerLocaleData(localeRo);
registerLocaleData(localeFi);
registerLocaleData(localeBe);
registerLocaleData(localeDe);
registerLocaleData(localeEn);

export function firebaseConfigFactory(platform: Platform,
                                      firebase: FirebaseService,
                                      authorizationService: AuthorizationService,
                                      localStorage: LocalStorageService): Supplier<Promise<boolean>> {
  HighchartsCustomizations.init();
  return () => new Promise<boolean>(resolve => {
    platform.ready()
      .then(() => firebase.setRemoteConfig())
      .then(() => authorizationService.startUpAsync())
      .then(() => localStorage.initStorage())
      .then(() => resolve(true));
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    provideIonicAngular({
      mode: 'ios'
    }),
    provideZoneChangeDetection(), importProvidersFrom(
      BrowserModule,
      AppRoutingModule,
      FormsModule,
      PipesModule,
      MatDialogModule,
      GeneralInfoPageModule,
      NgIdleModule.forRoot(),
      IonicStorageModule.forRoot(),
      AuthModule,
      LoggerModule.forRoot({
        level: environment.logLevel
      }),
      LetDirective
    ),
    EtHttpService,
    InAppBrowser,
    provideAppInitializer(() => {
      const initializerFn = (
        firebaseConfigFactory)(inject(Platform),
        inject(FirebaseService),
        inject(AuthorizationService),
        inject(LocalStorageService));
      return initializerFn();
    }),
    provideTranslateService({
      loader: {provide: TranslateLoader, useClass: BackendTranslateLoader}
    }),
    {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},
    {provide: LOCAL_STORAGE_TOKEN, useFactory: localStorageFactory},
    {provide: HTTP_INTERCEPTORS, useClass: GlobalTenantInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: AuthExpiredInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: ErrorHandlerInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: InternetConnectionInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: DeviceInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: AppVersionInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: ClientContextInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: SslPinningRequestInterceptor, multi: true},
    LocalStorageService,
    SecureStorage,
    HTTP,
    ChartDataPipe,
    CurrenciesService,
    HedgingProfileService,
    CompanyService,
    CacheService,
    SharedDataService,
    RxStompService,
    CallNumber,
    EmailComposer,
    Clipboard,
    AuthGuardService,
    JwtHelperService,
    FingerprintAIO,
    File,
    SocialSharing,
    Market,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    NumberFormatService,
    {
      provide: 'APP_INIT_PLUGIN',
      useFactory: (numberFormatService: NumberFormatService): (() => Promise<void>) => async (): Promise<void> => {
        await firstValueFrom(numberFormatService.ready$);
      }
    }
  ]
})
  .catch(err => console.log(err));
